import type { ProgressColor, ProgressStep } from 'components/ProgressIndicator';
import { useJobContext } from 'pages/Job/context';
import { useMemo } from 'react';
import { checkJobPaidByOB } from 'utils/Job-functions';
import {
  CANCELLED_FINANCE_STEPS,
  CHANGES_REQUESTED_STEPS,
  DECLINED_FINANCE_STEPS,
  DECLINED_NO_FINANCE_STEPS,
  STEPS,
} from './constants';
import { getProgressBarToShow } from './helpers';

export interface JobProgressBarHook {
  showBar: boolean;
  redBar: boolean;
  steps: ProgressStep[];
  isLoading: boolean;
  barColor: ProgressColor;
}

export default function useJobProgressBar(): JobProgressBarHook {
  const { job, archived, changesRequested, isLoading } = useJobContext();

  const paidByBank = useMemo(() => checkJobPaidByOB(job), [job]);

  const status = useMemo(() => {
    if (!job) return undefined;
    return job.status;
  }, [job]);

  const financeStatus = useMemo(() => {
    if (!job) return undefined;
    return job?.finance_status;
  }, [job]);

  const progressBarToShow = useMemo(() => {
    if (isLoading) return 'standard';
    if (!status || archived) return undefined;
    return getProgressBarToShow(status, financeStatus, changesRequested);
  }, [isLoading, status, financeStatus, archived, changesRequested]);

  const showBar = useMemo(
    () => Boolean(progressBarToShow),
    [progressBarToShow],
  );

  const redBar = useMemo(
    () => progressBarToShow !== 'standard',
    [progressBarToShow],
  );

  const barColor = useMemo(() => {
    if (progressBarToShow === 'changes_requested') return 'orange';
    return progressBarToShow !== 'standard' ? 'red' : 'green';
  }, [progressBarToShow]);

  const steps = useMemo(() => {
    if (progressBarToShow === 'changes_requested')
      return CHANGES_REQUESTED_STEPS;
    if (progressBarToShow === 'declined_no_finance')
      return DECLINED_NO_FINANCE_STEPS;
    if (progressBarToShow === 'declined_finance') return DECLINED_FINANCE_STEPS;
    if (progressBarToShow === 'cancelled_finance')
      return CANCELLED_FINANCE_STEPS;
    return STEPS.map(({ text, combinations }) => {
      if (!status && !financeStatus)
        return {
          text,
          active: false,
        };
      const adjustedFinanceStatus =
        paidByBank && (status === 'sent' || status === 'accepted')
          ? 'financed'
          : financeStatus;
      const active = combinations.some(
        (combo) =>
          combo.status === status &&
          combo.financeStatus === adjustedFinanceStatus,
      );
      return {
        text,
        active,
      };
    });
  }, [progressBarToShow, status, financeStatus, paidByBank]);

  return {
    showBar,
    redBar,
    steps,
    isLoading,
    barColor,
  };
}
